import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Card, Row, Col, Typography, Button, Spin, Statistic, Table, Tag } from 'antd';
import {
  ArrowLeftOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import moment from 'moment';
import { RequestTrackingResponse } from '@openapi';
import api from '@services/api';
import { formatNumber, formatTime } from './utils/trackingUtils';
import RequestDetailModal from './components/RequestDetailModal';
import './RequestTrackingDashboard.scss';

const { Title } = Typography;

const DeviceTrackingPage: React.FC = () => {
  const { macAddress } = useParams<{ macAddress: string }>();
  const history = useHistory();
  const [loading, setLoading] = useState<boolean>(false);
  const [deviceRequests, setDeviceRequests] = useState<RequestTrackingResponse[]>([]);
  const [devicePage, setDevicePage] = useState<number>(1);
  const [devicePageSize, setDevicePageSize] = useState<number>(10);
  const [deviceTotal, setDeviceTotal] = useState<number>(0);
  const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = useState<RequestTrackingResponse | null>(null);

  useEffect(() => {
    fetchDeviceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [macAddress, devicePage, devicePageSize]);

  const fetchDeviceData = async () => {
    if (!macAddress) return;

    setLoading(true);
    try {
      const response = await api.tracking.getRequestsByDevice({
        macAddress: decodeURIComponent(macAddress),
        page: devicePage - 1,
        pageSize: devicePageSize,
        limit: 10000,
      });

      if (response && response.data) {
        setDeviceRequests(response.data);
        setDeviceTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch device requests:', error);
      setDeviceRequests([]);
      setDeviceTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const getDeviceStatistics = () => {
    if (deviceRequests.length === 0 && deviceTotal === 0) return null;

    const totalRequests = deviceRequests.length;
    const successCount = deviceRequests.filter((r) => (r.responseStatus || 0) < 400).length;
    const errorCount = totalRequests - successCount;
    const successRate = totalRequests > 0 ? ((successCount / totalRequests) * 100).toFixed(1) + '%' : '0%';
    const errorRate = totalRequests > 0 ? ((errorCount / totalRequests) * 100).toFixed(1) + '%' : '0%';
    const executionTimes = deviceRequests.map((r) => r.executionTimeMs || 0);
    const avgExecutionTime = executionTimes.length > 0 ? executionTimes.reduce((a, b) => a + b, 0) / totalRequests : 0;

    return {
      totalRequests: deviceTotal,
      successCount,
      errorCount,
      successRate,
      errorRate,
      avgExecutionTime,
    };
  };

  const getDeviceChartData = () => {
    if (deviceRequests.length === 0) return { categories: [], executionTimes: [] };

    const sortedRequests = [...deviceRequests].sort((a, b) => {
      const timeA = a.requestTimestamp ? new Date(a.requestTimestamp).getTime() : 0;
      const timeB = b.requestTimestamp ? new Date(b.requestTimestamp).getTime() : 0;
      return timeA - timeB;
    });

    const categories = sortedRequests.map((r) =>
      r.requestTimestamp ? moment(r.requestTimestamp).format('MM/DD HH:mm') : '',
    );
    const executionTimes = sortedRequests.map((r) => r.executionTimeMs || 0);

    return { categories, executionTimes };
  };

  const showRequestDetail = (request: RequestTrackingResponse) => {
    setSelectedRequest(request);
    setDetailModalVisible(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalVisible(false);
    setSelectedRequest(null);
  };

  const deviceStats = getDeviceStatistics();
  const deviceChartData = getDeviceChartData();

  const deviceLineChartOptions: ApexOptions = {
    chart: {
      type: 'line',
      height: 350,
      toolbar: {
        show: true,
      },
      zoom: {
        enabled: true,
      },
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    markers: {
      size: 4,
      hover: {
        size: 6,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: deviceChartData.categories,
      title: {
        text: 'Request Time',
      },
      labels: {
        rotate: -45,
        rotateAlways: false,
      },
    },
    yaxis: {
      title: {
        text: 'Execution Time (ms)',
      },
    },
    colors: ['#1890ff'],
    title: {
      text: 'Response Time Trend',
      align: 'left',
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => `${val}ms`,
      },
    },
    grid: {
      borderColor: '#f1f1f1',
    },
  };

  const deviceLineSeries = [
    {
      name: 'Execution Time',
      data: deviceChartData.executionTimes,
    },
  ];

  const columns = [
    {
      title: 'API',
      dataIndex: 'apiVersion',
      key: 'apiVersion',
      render: (version: string) => <Tag color={version === 'v2' ? 'green' : 'orange'}>{version}</Tag>,
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      key: 'endpoint',
      render: (endpoint: string) => <Tag color="blue">{endpoint}</Tag>,
    },
    {
      title: 'Execution Time',
      dataIndex: 'executionTimeMs',
      key: 'executionTimeMs',
      render: (time: number) => (
        <span style={{ color: time > 5000 ? '#ff4d4f' : '#52c41a', fontWeight: 'bold' }}>{formatTime(time)}</span>
      ),
      sorter: (a: RequestTrackingResponse, b: RequestTrackingResponse) =>
        (a.executionTimeMs || 0) - (b.executionTimeMs || 0),
    },
    {
      title: 'Status',
      dataIndex: 'responseStatus',
      key: 'responseStatus',
      render: (status: number) => <Tag color={status < 400 ? 'success' : 'error'}>{status}</Tag>,
    },
    {
      title: 'Timestamp',
      dataIndex: 'requestTimestamp',
      key: 'requestTimestamp',
      render: (timestamp: Date) => moment(timestamp).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'State',
      dataIndex: 'deviceStatus',
      key: 'deviceStatus',
      render: (status: string) => {
        if (!status) return <Tag>N/A</Tag>;
        const colorMap: Record<string, string> = {
          POSTPONED: 'orange',
          PENDING: 'blue',
          UP_TO_DATE: 'green',
          NO_STATUS: 'default',
        };
        return <Tag color={colorMap[status] || 'default'}>{status.replace(/_/g, ' ')}</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right' as const,
      width: 100,
      render: (_: unknown, record: RequestTrackingResponse) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => showRequestDetail(record)} size="small">
          Details
        </Button>
      ),
    },
  ];

  if (loading && deviceRequests.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="Loading device tracking data..." />
      </div>
    );
  }

  return (
    <div className="request-tracking-dashboard">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => history.goBack()} style={{ marginRight: 16 }}>
            Back
          </Button>
        </Col>
        <Col flex="auto">
          <Title level={2} style={{ margin: 0 }}>
            Device Tracking: {decodeURIComponent(macAddress)}
          </Title>
        </Col>
      </Row>

      {deviceStats && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Requests"
                  value={formatNumber(deviceStats.totalRequests)}
                  prefix={<ApiOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Success Rate"
                  value={deviceStats.successRate}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                  suffix={`(${formatNumber(deviceStats.successCount)})`}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Error Rate"
                  value={deviceStats.errorRate}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                  suffix={`(${formatNumber(deviceStats.errorCount)})`}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Avg Response Time"
                  value={formatTime(deviceStats.avgExecutionTime)}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Response Time Trend" style={{ marginBottom: 24 }}>
            <ReactApexChart options={deviceLineChartOptions} series={deviceLineSeries} type="line" height={350} />
          </Card>

          <Card title="Request History">
            <Table
              dataSource={deviceRequests}
              columns={columns}
              rowKey="id"
              loading={loading}
              pagination={{
                current: devicePage,
                pageSize: devicePageSize,
                total: deviceTotal,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, newPageSize) => {
                  setDevicePage(page);
                  if (newPageSize !== devicePageSize) {
                    setDevicePageSize(newPageSize);
                    setDevicePage(1);
                  }
                },
              }}
              scroll={{ x: true }}
            />
          </Card>
        </>
      )}

      {!deviceStats && !loading && (
        <Card>
          <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
            <ApiOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <div>No requests found for device: {decodeURIComponent(macAddress)}</div>
          </div>
        </Card>
      )}

      <RequestDetailModal visible={detailModalVisible} request={selectedRequest} onClose={handleDetailModalClose} />
    </div>
  );
};

export default DeviceTrackingPage;
